import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronDown, ListPlus, Plus, Send, UserPlus } from "lucide-react";
import { cn } from "@/lib/cn";
import { TaskCreateDrawer } from "@/components/task/TaskCreateDrawer";
import { CreateInternalRequestDrawer } from "@/components/requests/CreateInternalRequestDrawer";
import { ScheduleMeetingDrawer } from "@/components/client360/ScheduleMeetingDrawer";
import { ClientPickerDrawer } from "@/components/home/ClientPickerDrawer";
import { MeetingTargetModeDrawer } from "@/components/meetings/MeetingTargetModeDrawer";
import { ProspectPickerDrawer } from "@/components/meetings/ProspectPickerDrawer";
import { ScheduleProspectMeetingDrawer } from "@/components/meetings/ScheduleProspectMeetingDrawer";
import { ContactCreateFlow } from "@/components/clients/ContactCreateFlow";
import type { HomeAllowedActions } from "@/api/types";

type Open =
  | { kind: "task" }
  | { kind: "request" }
  | { kind: "meeting-mode" }                                   // D-134 W1 — existing/prospect
  | { kind: "meeting-pick" }                                   // existing holding-picker
  | { kind: "meeting"; holdingId: string; holdingName: string }
  | { kind: "prospect-pick" }                                  // D-134 W2/W3 — lead search/create
  | { kind: "schedule-prospect"; accountId: string; name: string } // D-134 W4
  | { kind: "contact" }
  | null;

/**
 * Home/F6 R4 — «+ Створити» (D-031: canonical drawers ONLY, zero new forms).
 * Renders ONLY backend-allowed items (allowedActions backend-owned — §20 no dead
 * CTA; nothing allowed → the whole button is hidden).
 *   Задача          → TaskCreateDrawer (canonical, optional client)
 *   Запит у відділ  → 3B CreateInternalRequestDrawer (dept-queue, no holding ctx)
 *   Зустріч         → MeetingTargetModeDrawer (W1 existing/prospect) → existing: ClientPickerDrawer
 *                     → ScheduleMeetingDrawer · prospect (D-134): ProspectPickerDrawer →
 *                     ScheduleProspectMeetingDrawer (account-centric, holding=null)
 *   Контакт         → ContactCreateFlow (D-034: picker → canonical ContactDrawer;
 *                     per-holding canCreate gating is backend-owned inside the flow)
 */
export function CreateMenu({
  allowedActions,
  onDone,
}: {
  allowedActions: HomeAllowedActions;
  onDone: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // close on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const items = [
    allowedActions.task && {
      key: "task",
      label: "Задача",
      icon: ListPlus,
      onClick: () => setOpen({ kind: "task" }),
    },
    allowedActions.internal_request && {
      key: "request",
      label: "Запит у відділ",
      icon: Send,
      onClick: () => setOpen({ kind: "request" }),
    },
    allowedActions.meeting && {
      key: "meeting",
      label: "Зустріч",
      icon: CalendarPlus,
      // D-134 — уніфіковано зі сторінковою кнопкою: спершу «З ким зустріч?» (existing/prospect).
      onClick: () => setOpen({ kind: "meeting-mode" }),
    },
    // Контакт — core CRM action (3.0D ACL enforced server-side; the flow shows an
    // honest backend-owned notice when canCreate=false for the picked client).
    {
      key: "contact",
      label: "Контакт",
      icon: UserPlus,
      onClick: () => setOpen({ kind: "contact" }),
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    icon: typeof ListPlus;
    onClick: () => void;
  }>;

  // §20: no allowed actions → no button at all (never a dead/disabled CTA).
  if (items.length === 0) return null;

  const close = () => {
    setOpen(null);
    onDone(); // invalidate home queue — covers create side-effects
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        <Plus className="h-4 w-4" /> Створити
        <ChevronDown className="h-3.5 w-3.5 opacity-80" />
      </button>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1.5 min-w-[200px] rounded-lg border bg-popover p-1 shadow-lg"
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  it.onClick();
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm",
                  "hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none",
                )}
              >
                <Icon className="h-4 w-4 text-primary" /> {it.label}
              </button>
            );
          })}
        </div>
      )}

      {/* canonical drawers (D-031) */}
      {open?.kind === "task" && (
        <TaskCreateDrawer open onClose={close} />
      )}
      {open?.kind === "request" && (
        <CreateInternalRequestDrawer open onClose={close} />
      )}
      {/* D-134 W1 — «З ким зустріч?» (той самий drawer, що сторінкова кнопка) */}
      {open?.kind === "meeting-mode" && (
        <MeetingTargetModeDrawer
          open
          onClose={() => setOpen(null)}
          onExisting={() => setOpen({ kind: "meeting-pick" })}
          onProspect={() => setOpen({ kind: "prospect-pick" })}
        />
      )}
      {open?.kind === "meeting-pick" && (
        <ClientPickerDrawer
          open
          onClose={() => setOpen(null)}
          title="Зустріч — оберіть клієнта"
          hint="Зустріч планується в контексті клієнта (контакти підтягнуться з його картки)."
          onPick={(holdingId, holdingName) => setOpen({ kind: "meeting", holdingId, holdingName })}
        />
      )}
      {open?.kind === "meeting" && (
        <ScheduleMeetingDrawer open onClose={close} holdingId={open.holdingId} />
      )}
      {/* D-134 W2/W3/W4 — prospect search/create → account-centric meeting */}
      {open?.kind === "prospect-pick" && (
        <ProspectPickerDrawer
          open
          onClose={() => setOpen(null)}
          onPick={(accountId, name) => setOpen({ kind: "schedule-prospect", accountId, name })}
        />
      )}
      {open?.kind === "schedule-prospect" && (
        <ScheduleProspectMeetingDrawer
          open
          accountId={open.accountId}
          prospectName={open.name}
          onCreated={onDone}
          onClose={close}
        />
      )}
      {open?.kind === "contact" && (
        <ContactCreateFlow open onClose={close} />
      )}
    </div>
  );
}
